import { ReactNode } from 'react';
import { Box, ButtonBase, Tooltip } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';

import { GameElementIcon } from '../../shared/GameElementPicker/GameElementIcon';
import { Icon } from '../../shared/Icon';

/**
 * Shared visual tile for the giftsv2 area. Both the Scheduler and the Planner
 * render gifts/subtasks with this component so the two pages stay visually in
 * sync.
 *
 * The tile is "icon first": when a game-element id is supplied it renders the
 * matching {@link GameElementIcon}. Only when no game element is available does
 * it fall back to an attachment image (`fallbackImage`), and finally to a
 * neutral placeholder.
 */

const BIG_SIZE = 48;
const SMALL_SIZE = 24;

interface ElementTileProps {
    /** First element id is shown as the main image, while the remaining ones will be shown as small overlay thumbnails */
    gameElementIds?: string[];
    /**
     * Attachment image used only when `bigId` is absent — keeps the tile
     * "icon first, attachment as fallback".
     */
    fallbackImage?: string | null;
    size?: number;
    tooltip: ReactNode;
    onClick?: () => void;
    ariaLabel?: string;
    /** Corner badge (e.g. a status marker). */
    badge?: ReactNode;
    badgePosition?: 'top-left' | 'top-right';
    /** Overrides for the outer tile (outline / background accent). */
    sx?: SxProps<Theme>;
    /** Overrides for the big-icon area (background / opacity). */
    bigSx?: SxProps<Theme>;
}

export const ElementTile = ({
    gameElementIds = [],
    fallbackImage,
    size = BIG_SIZE,
    tooltip,
    onClick,
    ariaLabel,
    badge,
    badgePosition = 'top-right',
    sx,
    bigSx,
}: ElementTileProps) => {
    const renderBig = () => {
        if (gameElementIds.length > 0) {
            return (
                <GameElementIcon
                    gameElementId={gameElementIds[0]}
                    size={size}
                />
            );
        }
        if (fallbackImage) {
            return (
                <Box
                    component="img"
                    src={fallbackImage}
                    alt=""
                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
            );
        }
        return (
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'text.disabled',
                    fontSize: size * 0.5,
                }}
            >
                <Icon name="card_giftcard" />
            </Box>
        );
    };

    return (
        <Tooltip arrow title={tooltip}>
            <ButtonBase
                aria-label={ariaLabel}
                onClick={onClick}
                sx={{
                    width: size,
                    height: size,
                    borderRadius: 1.5,
                    position: 'relative',
                    overflow: 'visible',
                    border: 1,
                    borderColor: 'divider',
                    transition: 'transform .1s, box-shadow .1s',
                    '&:hover': {
                        transform: 'scale(1.06)',
                        boxShadow: 3,
                        zIndex: 2,
                    },
                    ...sx,
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        borderRadius: 1.5,
                        overflow: 'hidden',
                        display: 'flex',
                        bgcolor: 'grey.100',
                        ...bigSx,
                    }}
                >
                    {renderBig()}
                </Box>

                {gameElementIds.length > 1 && (
                    <Box
                        sx={{
                            position: 'absolute',
                            right: 1,
                            bottom: 1,
                            display: 'flex',
                            alignItems: 'flex-end',
                            '& > *:not(:first-of-type)': { ml: '-6px' },
                        }}
                    >
                        {gameElementIds.slice(1).map((id) => (
                            <SmallThumb key={id} id={id} />
                        ))}
                    </Box>
                )}

                {badge && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: -6,
                            ...(badgePosition === 'top-left'
                                ? { left: -6 }
                                : { right: -6 }),
                        }}
                    >
                        {badge}
                    </Box>
                )}
            </ButtonBase>
        </Tooltip>
    );
};

/** A small circular game-element thumbnail overlaid on the tile. */
const SmallThumb = ({ id }: { id: string }) => (
    <Box
        sx={{
            width: SMALL_SIZE,
            height: SMALL_SIZE,
            borderRadius: '50%',
            overflow: 'hidden',
            flexShrink: 0,
            display: 'flex',
            bgcolor: 'grey.100',
            boxSizing: 'border-box',
        }}
    >
        <GameElementIcon gameElementId={id} size={SMALL_SIZE} />
    </Box>
);
